import type { EligibilityVerdict } from '@kiku/scoring/types';
import { MessageSquare, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScoreOverrideDialog } from './score-override-dialog';
import { scrollToTranscript } from './scoring-utils';

interface ExpectationsResultProps {
  questionScoreId: string;
  questionId: string;
  questionText: string;
  verdict: EligibilityVerdict | null;
  reasoning: string;
  evidence: string;
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
}

export function ExpectationsResult({
  questionScoreId,
  questionId,
  questionText,
  verdict,
  reasoning,
  evidence,
  orgId,
  deptId,
  jobId,
  applicationId
}: ExpectationsResultProps): React.ReactNode {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">{t('scoring.expectations')}</h2>
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm border-l-4 border-l-[var(--color-section-green)]">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium">{questionText}</p>
          <button
            type="button"
            className="group/score shrink-0 flex items-center gap-1 cursor-pointer rounded px-1 -mx-1 hover:bg-muted/50"
            onClick={() => {
              setDialogOpen(true);
            }}
          >
            <span
              className={`text-sm font-medium ${verdict === 'pass' ? 'text-green-600' : 'text-red-600'}`}
            >
              {t(`scoring.verdicts.${verdict ?? 'fail'}` as 'scoring.verdicts.pass')}
            </span>
            <Pencil className="size-3 text-muted-foreground opacity-0 group-hover/score:opacity-100 transition-opacity" />
          </button>
        </div>
        {(reasoning || evidence) && (
          <div className="mt-2 flex flex-col gap-1">
            {reasoning && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{t('scoring.reasoning')}:</span> {reasoning}
              </p>
            )}
            {evidence && (
              <p className="text-sm italic text-muted-foreground">&ldquo;{evidence}&rdquo;</p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            scrollToTranscript(questionId);
          }}
          className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <MessageSquare className="size-3" />
          {t('scoring.viewInTranscript')}
        </button>
      </div>

      <ScoreOverrideDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        questionScoreId={questionScoreId}
        questionText={questionText}
        currentValue={verdict ?? 'fail'}
        mode="expectations"
        orgId={orgId}
        deptId={deptId}
        jobId={jobId}
        applicationId={applicationId}
      />
    </div>
  );
}
